
import { APP_CONFIG } from "./config";
import { AnalysisOrigin } from "./analysisTypes";
import { IntegrityStatus } from "../contracts/scoring";

/**
 * INTEGRITY GATE - NO-SIM ENFORCED v3.0 (PRE-RECORDING PROOF)
 */

export interface IntegrityDecision {
    status: IntegrityStatus;
    reason: string;
}

// Fix: Added missing checkAntiReplay function required by integrity tests to honor configuration settings
export function checkAntiReplay(stuHash: string, refHash: string): string | null {
    if (!APP_CONFIG.INTEGRITY_RULES.enableAntiReplay) return null;
    if (refHash && refHash !== "none" && stuHash === refHash) {
        return "IDENTICAL_FILES";
    }
    return null;
}

export function integrityGate(params: {
  origin: AnalysisOrigin;
  stuHash: string;
  refHash: string;
  captureToken?: string;
  mode?: "ANALYZE" | "ARENA" | "LEAGUE";
}): IntegrityDecision {
  
  // 1. Détection de Simulation (Source logicielle interdite)
  if (params.origin === "SIMULATION") {
      return { status: "FAIL", reason: "SIMULATION_STRICTLY_FORBIDDEN" };
  }

  // 2. Anti-Replay Binaire (Hash SHA-256 identique)
  // Fix: Use checkAntiReplay helper to properly handle binary replay detection with config checks
  const replayError = checkAntiReplay(params.stuHash, params.refHash);
  if (replayError) {
      return { status: "FAIL", reason: "BINARY_REPLAY_DETECTED" };
  }

  // 3. Validation de la capture Live (Obligatoire pour compétition)
  // Fix: Added specific check for competition modes to enforce live recording origin as expected by tests
  if (params.mode === "ARENA" || params.mode === "LEAGUE") {
      if (params.origin !== "REAL_AUDIO" && params.origin !== "SKILL_ASSESSMENT") {
          return { status: "FAIL", reason: "LIVE_RECORDING_REQUIRED_FOR_COMPETITION" };
      }
      if (!params.captureToken) {
          return { status: "FAIL", reason: "CERTIFIED_CAPTURE_TOKEN_MISSING" };
      }
  }

  return { status: "PASS", reason: "SIGNAL_SOURCE_VALIDATED" };
}

/**
 * checkSpectralSimilarity
 * Seuil durci à 0.990 pour détecter les fichiers "presque identiques" (ré-encodés).
 */
export function checkSpectralSimilarity(stuFingerprint: number[], refFingerprint: number[]): IntegrityDecision {
    if (!stuFingerprint?.length || !refFingerprint?.length) {
        return { status: "UNVERIFIABLE", reason: "NO_REFERENCE_DATA" };
    }

    let dot = 0; let magS = 0; let magR = 0;
    const n = Math.min(stuFingerprint.length, refFingerprint.length);
    
    for(let i=0; i<n; i++) {
        dot += stuFingerprint[i] * refFingerprint[i];
        magS += stuFingerprint[i] * stuFingerprint[i];
        magR += refFingerprint[i] * refFingerprint[i];
    }
    
    const correlation = dot / (Math.sqrt(magS) * Math.sqrt(magR) || 1);

    // CRITICAL CLONE GATE
    if (correlation > 0.990) {
        return { status: "FAIL", reason: "SPECTRAL_CLONE_DETECTED_ANTI_CHEAT" };
    }

    return { status: "PASS", reason: "UNIQUE_SIGNAL_CERTIFIED" };
}
